/**
 * Apply a client company's branding to the shell, after login.
 *
 * Before login the shell is painted with the MSP's branding (the 5c resolve
 * endpoint, held in `window.__branding` when that shell code is present).
 * Once the user is known, their primary company may have overrides — a
 * different name, logo or colour — and those are laid on top. The MSP's look
 * is the base at every step, so a company that overrides nothing changes
 * nothing.
 *
 * What is applied is deliberately small: the document title, a handful of
 * CSS custom properties on <html> that the shell (5c) can consume as it
 * learns to, and the branding object itself on `window.__companyBranding`
 * for anything that wants to read it. The sidebar logo is React's job
 * (DashboardLayout reads the same object), because a logo swapped by DOM
 * surgery would be undone on the next render.
 */

/** The branding the shell loaded before login, if the 5c code is present. */
export function shellBranding() {
  if (typeof window === 'undefined') return null;
  const b = window.__branding;
  if (!b || typeof b !== 'object') return null;
  // The pre-login object uses Portal Settings' names; normalise to ours.
  return {
    portalName: b.portalName || b.companyName || '',
    logoUrl: b.logoUrl || b.companyLogoUrl || '',
    primaryColor: b.primaryColor || '',
    wallpaperUrl: b.wallpaperUrl || b.loginBackgroundUrl || '',
    supportEmail: b.supportEmail || '',
    supportPhone: b.supportPhone || '',
    fromName: b.fromName || '',
    locale: b.locale || '',
    currency: b.currency || '',
  };
}

/**
 * Paint the effective branding. Idempotent; safe to call on every change.
 * Returns the object it applied so a caller can keep it in state.
 */
export function applyCompanyBranding(branding) {
  if (typeof document === 'undefined' || !branding) return branding;
  const root = document.documentElement;

  if (branding.portalName) document.title = branding.portalName;

  if (branding.primaryColor) {
    root.style.setProperty('--brand-primary', branding.primaryColor);
    // A darker step for hover states; cheap and good enough for a flat
    // brand colour. Anything subtler belongs in the shell's own theming.
    root.style.setProperty('--brand-primary-dark', darken(branding.primaryColor, 0.12));
  } else {
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-primary-dark');
  }

  if (branding.wallpaperUrl) root.style.setProperty('--brand-wallpaper', `url("${branding.wallpaperUrl}")`);
  else root.style.removeProperty('--brand-wallpaper');

  window.__companyBranding = branding;
  return branding;
}

/** Back to the pre-login look (logout). */
export function resetCompanyBranding() {
  if (typeof document === 'undefined') return;
  const base = shellBranding();
  if (base) {
    applyCompanyBranding(base);
  } else {
    const root = document.documentElement;
    for (const p of ['--brand-primary', '--brand-primary-dark', '--brand-wallpaper']) root.style.removeProperty(p);
  }
  window.__companyBranding = null;
}

/** `#rrggbb` darkened by `amount` (0..1). Returns the input if it is not hex. */
function darken(hex, amount) {
  const m = /^#([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (shift) => Math.max(0, Math.round(((n >> shift) & 0xff) * (1 - amount)));
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('')}`;
}
